'use client';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { RichText } from '../../../components/RichText';
import Tooltip from '../../../components/Tooltip';
import { CmsProduct } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../utils/analytics';

type ProductDetailsTableKeyValueRowProps = {
  label: string;
  icon?: JSX.Element;
  value?: string;
  additionalInformation?: string;
  hiddenBorder?: boolean;
  /** Parent accordion/subsection title, used to build the tooltip tracking title. */
  sectionTitle?: string;
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
};

export const ProductDetailsTableKeyValueRow: React.FC<
  ProductDetailsTableKeyValueRowProps
> = ({
  label,
  value,
  additionalInformation,
  hiddenBorder,
  icon,
  sectionTitle,
  product,
  pushDataLayer,
}) => {
  // Product-list name for the tooltip events, aligned with the rest of the PDP
  // (page path) so item_list_name is populated instead of undefined.
  const itemListName = usePathname() ?? undefined;

  // Tooltip open tracking (#2406 hover / #2407 click). The generic Tooltip
  // reports which trigger opened it and we map it 1:1 to the analytics action.
  // These rows use the default hover-mode tooltip, so a mobile tap opens it via
  // the hover path and is reported as HOVER (tap ≈ hover); the click branch
  // applies only if a tooltip is used in click mode.
  const handleTooltipOpen = (trigger: 'hover' | 'click') => {
    if (!product || !pushDataLayer) {
      return;
    }
    sendProductAnalytics({
      event: 'product_interaction',
      action: trigger === 'click' ? UserActionEnum.CLICK : UserActionEnum.HOVER,
      componentType: ComponentTypeEnum.TOOLTIP,
      componentTitle: `Product Details Module/${
        sectionTitle ? `${sectionTitle}_` : ''
      }${label}`,
      itemListName,
      product,
      index: '0',
      pushDataLayer,
    });
  };

  return (
    <div>
      {/* CMS-1810: on mobile the row is a fixed 150px name column + a fill-width,
          right-aligned value; from md up we revert to the original content-width
          layout so desktop/tablet stay unchanged. */}
      <div
        className={classNames(
          'flex justify-between gap-spacing/gap/x md:gap-spacing/gap/y-xs',
          {
            'border-b border-borders/default pb-spacing/gap/y-xs': !hiddenBorder,
          },
        )}
      >
        <div
          className={classNames(
            'mhg-alpha-typography-body-2-relaxed text-text/secondary-lighter',
            // Only pin the name column to 150px when there is a value to align
            // against; value-less sections (e.g. highlights) use the full width.
            // 150px is a fixed absolute px per Figma (no design token exists).
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            { 'w-[150px] shrink-0 md:w-auto md:shrink': value },
          )}
        >
          <div className="flex items-center gap-spacing/gap/y-xs relative">
            {icon && icon}
            <div className={classNames({ 'ml-10': icon })}>
              <RichText>{label}</RichText>
            </div>
          </div>
        </div>
        <div
          className={classNames('flex items-center gap-2', {
            'flex-1 justify-end text-right md:flex-initial md:justify-start md:text-left':
              value,
          })}
        >
          <div className="mhg-alpha-typography-body-2-relaxed text-text/primary">
            {value}
          </div>
          {additionalInformation && (
            <Tooltip
              placement="left"
              content={additionalInformation}
              onOpen={handleTooltipOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};
